import { AnimationAction, Object3D } from 'three'
import { sample } from '@/app/utils/functions'
import { create } from 'zustand'

export const actionNames: Record<string, string> = {
    standby: '1-1Standby',
    lookAround: '1-2LookAround',
    takePaper1: '2-1TakePaper_B',
    takePaper2: '2-2TakePaper_A',
    turnAround: '3-0Turn360',
    fighting: '3-1Fighting',
    blink: '3-2Blink',
    touchFace: '3-3TouchFace',
    cute: '4-1Cute',
    shy: '4-2Shy',
}
export const actionList: Record<'idle' | 'takePaper' | 'posing' | 'pet' | 'success', string[]> = {
    idle: ['lookAround', 'fighting', 'blink', 'touchFace', 'cute', 'shy'],
    takePaper: ['takePaper1', 'takePaper2'],
    posing: ['fighting', 'blink', 'touchFace'],
    pet: ['cute', 'shy'],
    success: ['blink', 'touchFace'],
}

type ModelStore = {
    visible: boolean
    actions: Record<string, AnimationAction> | null
    currentAction: string | null
    standbyTimeoutId: NodeJS.Timeout | null

    setVisible: (v: boolean) => void
    setCurrentAction: (name: string | null) => void
    setActions: (actions: Record<string, AnimationAction>) => void
    clearStandbyTimeout: () => void
    playAction: (
        key: keyof typeof actionNames,
        onFinish?: () => void,
        freeze?: boolean,
    ) => void
    posing: () => void
}
const useModel = create<ModelStore>((set, get) => ({
    visible: false,
    actions: null,
    currentAction: null,
    standbyTimeoutId: null,

    setVisible: (visible) => set({ visible }),
    setCurrentAction: (currentAction) => set({ currentAction }),
    setActions: (actions) => set({ actions }),
    clearStandbyTimeout: () => {
        const id = get().standbyTimeoutId
        if (id) {
            clearTimeout(id)
            set({ standbyTimeoutId: null })
        }
    },
    playAction: (key, onFinish, freeze = false) => {
        const actions = get().actions
        const action = actions?.[actionNames[key]]

        if (!actions) {
            console.warn('model.actions is null')
            return
        }
        if (!action) {
            console.warn(`model.playAction(): action "${key}" not found`)
            return
        }

        const mixer = action.getMixer()
        const model: Object3D = action.getRoot()

        model.visible = true
        action.clampWhenFinished = true
        mixer.stopAllAction()

        // remove all finished listeners
        const finishedListeners = (mixer as any)._listeners?.finished
        finishedListeners?.map((listener: () => void) => {
            mixer.removeEventListener('finished', listener)
        })
        // clear standby timeout
        get().clearStandbyTimeout()

        const handleFinished = () => {
            set({ currentAction: null })
            onFinish?.()
            if (!freeze) {
                const id = setTimeout(() => {
                    mixer.stopAllAction()
                    actions?.[actionNames.standby]?.reset().play()
                    set({currentAction: 'standby'})
                }, 2000)
                set({ standbyTimeoutId: id })
            }
        }

        mixer.addEventListener('finished', handleFinished)

        if (key !== 'standby') {
            action.setLoop(null, 1)
        }

        action.reset().play()
        set({ currentAction: key })
    },
    posing: () => {
        get().playAction(sample(actionList.posing), null, true)
    },
}))

export default useModel